import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Calendar, Users, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import Button from '../ui/button';

// Utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism Styles (consistent with other components)
const glassmorphism =
  'bg-white/70 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg';
const darkText = 'text-gray-900'; // For general text on light backgrounds
const lightText = 'text-white'; // For text on darker backgrounds

// Mock Data for shifts (can be replaced with props from parent or fetched data)
// In a real application, you might fetch these shifts from an API.
const mockShifts = [
  { id: 'all-shifts', name: 'All Shifts', startTime: '', endTime: '' },
  {
    id: 'shift1',
    name: 'Shift - 1',
    startTime: '12:00 AM',
    endTime: '8:00 AM',
  },
  { id: 'shift2', name: 'Shift - 2', startTime: '8:00 AM', endTime: '4:00 PM' },
  {
    id: 'shift3',
    name: 'Shift - 3',
    startTime: '4:00 PM',
    endTime: '12:00 AM',
  },
];

/**
 * SiteScheduleComponent manages and displays the schedule for a selected site.
 * It allows viewing and potentially assigning/unassigning employees to shifts.
 *
 * @param {Object} site - The currently selected site object.
 * @param {Object[]} employees - All available employees.
 * @param {function(): void} onClose - Callback to close the schedule view.
 */
const SiteScheduleComponent = ({ site, employees = [], onClose }) => {
  // State to manage the currently selected shift
  const [selectedShiftId, setSelectedShiftId] = useState('all-shifts'); // Default to 'All Shifts'
  const [shifts, setShifts] = useState(mockShifts); // Using mock shifts for now

  // State for managing assigned employees for the *currently selected shift*
  // This would typically come from site.shifts or be fetched via API
  const [currentShiftAssignedEmployees, setCurrentShiftAssignedEmployees] =
    useState([]);

  // Filter employees based on the selected shift
  const getFilteredEmployees = () => {
    if (!site || !employees) return [];

    // For demonstration, let's assume some employees are "assigned" to shifts.
    // In a real app, this logic would involve actual shift assignments from your backend.
    let filtered = employees.filter(emp => emp.officeId === site.id);

    if (selectedShiftId === 'all-shifts') {
      return filtered;
    } else {
      // This is a placeholder for actual shift assignment logic.
      // For now, let's just show a subset of employees for specific shifts.
      // You would replace this with logic that checks if an employee is assigned to `selectedShiftId`.
      if (selectedShiftId === 'shift1') {
        return filtered.filter(
          emp => emp.name.includes('John') || emp.name.includes('Jane')
        );
      } else if (selectedShiftId === 'shift2') {
        return filtered.filter(
          emp => emp.name.includes('Alice') || emp.name.includes('Bob')
        );
      } else if (selectedShiftId === 'shift3') {
        return filtered.filter(
          emp => emp.name.includes('Charlie') || emp.name.includes('David')
        );
      }
      return [];
    }
  };

  const displayedEmployees = getFilteredEmployees();

  // Handler for toggling employee assignment (mock logic)
  const onToggleAssign = employeeId => {
    setCurrentShiftAssignedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.3 }}
        className={combineClasses(
          glassmorphism,
          'p-6 w-full max-w-2xl space-y-6 overflow-y-auto'
        )}
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-300">
          <Typography
            variant="h5"
            className={combineClasses(
              darkText,
              'font-bold flex items-center gap-2'
            )}
          >
            <Calendar className="w-6 h-6" /> Site Schedule:{' '}
            {site?.name || 'N/A'}
          </Typography>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-red-500/20 text-red-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Shift Selection Dropdown */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="shift-select"
            className={combineClasses(darkText, 'font-medium')}
          >
            Select Shift:
          </label>
          <select
            id="shift-select"
            value={selectedShiftId}
            onChange={e => setSelectedShiftId(e.target.value)}
            className={combineClasses(
              'w-full p-2 rounded-md bg-white/80 border border-gray-300 text-gray-900',
              'focus:outline-none focus:ring-2 focus:ring-blue-500'
            )}
          >
            {shifts.map(shift => (
              <option key={shift.id} value={shift.id}>
                {shift.name}{' '}
                {shift.startTime && shift.endTime
                  ? `(${shift.startTime} - ${shift.endTime})`
                  : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Shifts List (or Employees assigned to selected shift) */}
        <div className="space-y-4">
          <Typography
            variant="h6"
            className={combineClasses(
              darkText,
              'font-semibold flex items-center gap-2'
            )}
          >
            <Users className="w-5 h-5" /> Employees for{' '}
            {shifts.find(s => s.id === selectedShiftId)?.name ||
              'Selected Shift'}
          </Typography>
          {displayedEmployees.length > 0 ? (
            displayedEmployees.map(employee => (
              <div
                key={employee.id}
                className="flex justify-between items-center p-3 bg-white/80 rounded-lg shadow-sm border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={employee.image}
                    alt={employee.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <Typography
                      variant="body1"
                      className="text-neutral-900 font-semibold"
                    >
                      {employee.name}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      {employee.note}
                    </Typography>
                  </div>
                </div>
                <Button
                  onClick={() => onToggleAssign(employee.id)}
                  className={combineClasses(
                    currentShiftAssignedEmployees.includes(employee.id)
                      ? 'bg-red-500/20 text-red-600 hover:bg-red-500/30'
                      : 'bg-green-500/20 text-green-600 hover:bg-green-500/30'
                  )}
                >
                  {currentShiftAssignedEmployees.includes(employee.id)
                    ? 'Unassign'
                    : 'Assign'}
                </Button>
              </div>
            ))
          ) : (
            <Typography
              variant="body2"
              className="text-center text-gray-600 p-4"
            >
              No employees found for this shift.
            </Typography>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button
            type="button"
            onClick={onClose}
            className="bg-gray-500/20 text-gray-700 hover:bg-gray-500/30"
          >
            Done
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default SiteScheduleComponent;
